/**
 * In-memory handoff for pipeline player deep links (SE-7145).
 * @remarks Why the URL is not enough. A newsfeed click navigates in-app to
 * `/player-kanban?boardId=…&playerId=…`, but the board screen stays mounted
 * across navigations and Expo Router on web strips params it does not
 * recognize — so `useLocalSearchParams` only *sometimes* reports the new
 * `playerId`, and a value-equality latch cannot distinguish "clicked the
 * same player again" from "nothing happened". The clicking surface knows
 * exactly what it just asked for, so it hands the intent over directly:
 * each click records a request with a fresh token, and the board consumes
 * each token at most once. Full-reload arrivals still ride the URL/seed
 * path in `usePlayerDeepLink`; this store only exists for in-app clicks.
 *
 * Built on an Apollo reactive var — the repo's pattern for module-level
 * reactive state (`src/stores/*`) — so the hook subscribes with
 * `useReactiveVar` and the functional-immutability lint holds.
 * @module features/player-kanban/utils/playerDeepLinkRequests
 */

import { makeVar } from "@apollo/client";

/** One click's worth of deep-link intent. */
export interface PlayerDeepLinkRequest {
  /** The player whose drawer should open. */
  readonly playerId: string;
  /**
   * The board the click targeted, when the link carried one. The consumer
   * holds the request until this board is the active one, so a click that
   * also switches boards cannot be evaluated against the outgoing board's
   * card pool.
   */
  readonly boardId?: string;
  /** Monotonic per-click identity — same player twice is two requests. */
  readonly token: number;
}

/** The latest unconsumed request, or `null`. */
export const playerDeepLinkRequestVar = makeVar<PlayerDeepLinkRequest | null>(
  null
);

/** The last token handed out, so consumed requests keep the sequence. */
const lastTokenVar = makeVar<number>(0);

/**
 * Record a click's deep-link intent.
 * @param target - The player (and optionally board) the click asked for.
 * @param target.playerId - The player whose drawer should open.
 * @param target.boardId - The board the link targeted, if any.
 */
export function requestPlayerDeepLink(target: {
  playerId: string;
  boardId?: string;
}): void {
  const token = lastTokenVar() + 1;
  lastTokenVar(token);
  playerDeepLinkRequestVar({
    playerId: target.playerId,
    boardId: target.boardId,
    token,
  });
}

/**
 * Mark a request handled, clearing it so it can never replay — e.g. against
 * a later remount of the board screen. A stale token (a newer click already
 * replaced the request) is a no-op.
 * @param token - The token of the request that was applied.
 */
export function consumePlayerDeepLinkRequest(token: number): void {
  if (playerDeepLinkRequestVar()?.token !== token) return;
  playerDeepLinkRequestVar(null);
}

/**
 * Test-only: drop any pending request and restart the token sequence so
 * suites cannot leak clicks into each other.
 */
export function resetPlayerDeepLinkRequestsForTests(): void {
  playerDeepLinkRequestVar(null);
  lastTokenVar(0);
}
